import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Send, Bot, User as UserIcon, Sparkles, AlertCircle, Link as LinkIcon, BookOpen } from "lucide-react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { Section } from "../components/Common";
import PractitionerRecommendations from "../components/PractitionerRecommendations";
import { COUNTRY_LIST } from "../lib/countries";
import { errorMessage } from "../lib/errors";

export default function AICoachPage() {
  const { t, i18n } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [country, setCountry] = useState("");
  const [conversationId, setConversationId] = useState("");
  const [recs, setRecs] = useState(null);
  const [crisis, setCrisis] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = async (preset) => {
    const message = (preset || text).trim();
    if (!message || sending) return;
    setSending(true);
    setMessages((m) => [...m, { role: "user", content: message }]);
    setText("");
    try {
      const { data } = await API.post("/coach/chat", { message, conversation_id: conversationId || undefined, language: i18n.language, country: country || undefined });
      setConversationId(data.conversation_id);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: data.answer,
          recommendations: data.recommendations,
          crisis_detected: data.crisis_detected,
          // Phase 7.4 — RAG citations + KB-used flag for source attribution
          citations: data.citations || [],
          kb_used: !!data.kb_used,
        },
      ]);
      setRecs(data.recommendations);
      setCrisis(!!data.crisis_detected);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "⚠️ " + errorMessage(e, t("coach.error_again")) }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="page-coach">
      <Section eyebrow={t("ask_donny.eyebrow")} title={t("coach.title")} subtitle={t("coach.subtitle")}>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <select value={country} onChange={(e) => setCountry(e.target.value)} className="bg-[#0F1116] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="coach-country">
            <option value="">{t("coach.country_optional")}</option>
            {COUNTRY_LIST.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)}
          </select>
          <div className="text-xs text-white/55"><Sparkles className="w-3 h-3 inline mr-1 gold-text" />{t("coach.disclaimer")}</div>
        </div>

        {messages.length === 0 && (
          <div className="card-surface p-4 mb-4" data-testid="coach-suggestions">
            <div className="text-xs uppercase tracking-[0.24em] gold-text mb-3">{t("coach.start_with")}</div>
            <div className="flex flex-wrap gap-2">
              {t("coach.sample_prompts", { returnObjects: true }).map((p, i) => (
                <button key={i} onClick={() => send(p)} className="chip hover:bg-white/10" data-testid={`prompt-${i}`}>{p}</button>
              ))}
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div ref={scrollRef} className="card-surface p-4 md:p-6 h-[55vh] overflow-y-auto" data-testid="coach-thread">
              {messages.map((m, i) => (
                <div key={i} className={`flex gap-3 mb-5 ${m.role === "user" ? "justify-end" : ""}`}>
                  {m.role !== "user" && (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E2C77A] to-[#7A5F2A] grid place-items-center shrink-0"><Bot className="w-4 h-4 text-black" /></div>
                  )}
                  <div className={`max-w-[80%] p-3 rounded-xl ${m.role === "user" ? "bg-white text-black" : "bg-white/5"}`}>
                    <div className="prose-coach text-sm">{m.content}</div>
                    {m.kb_used && m.citations && m.citations.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/10" data-testid="coach-citations">
                        <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-[0.18em] gold-text">
                          <BookOpen className="w-3 h-3" />
                          {t("coach.sources_label", "From the DONiTSH!T library")}
                        </div>
                        <ol className="space-y-1.5 text-xs text-white/75">
                          {m.citations.map((cit) => (
                            <li key={`c-${cit.index}-${cit.chunk_id}`} className="flex gap-2 items-start" data-testid={`coach-citation-${cit.index}`}>
                              <span className="gold-text font-mono text-[10px] mt-0.5">[{cit.index}]</span>
                              <div className="min-w-0">
                                <div className="truncate font-medium text-white/90">{cit.title}</div>
                                {Array.isArray(cit.heading_path) && cit.heading_path.length > 0 && (
                                  <div className="text-[10px] text-white/50 truncate">
                                    {cit.heading_path.join(" \u203a ")}
                                  </div>
                                )}
                              </div>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                    {m.crisis_detected && (
                      <div className="mt-3 text-rose-300 text-xs flex items-center gap-2"><AlertCircle className="w-3 h-3" />{t("coach.crisis_note")}</div>
                    )}
                  </div>
                  {m.role === "user" && (
                    <div className="w-8 h-8 rounded-full bg-white/10 grid place-items-center shrink-0"><UserIcon className="w-4 h-4" /></div>
                  )}
                </div>
              ))}
              {sending && (
                <div className="flex gap-3 mb-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E2C77A] to-[#7A5F2A] grid place-items-center"><Bot className="w-4 h-4 text-black" /></div>
                  <div className="bg-white/5 p-3 rounded-xl text-sm flex gap-1 items-center">
                    <span className="w-2 h-2 rounded-full bg-white/60 animate-pulse" />
                    <span className="w-2 h-2 rounded-full bg-white/60 animate-pulse" style={{ animationDelay: "0.15s" }} />
                    <span className="w-2 h-2 rounded-full bg-white/60 animate-pulse" style={{ animationDelay: "0.3s" }} />
                  </div>
                </div>
              )}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-4 flex gap-2">
              <input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("coach.placeholder")} className="flex-1 bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#C8A24A]" data-testid="coach-input" />
              <button type="submit" disabled={sending || !text.trim()} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="coach-send"><Send className="w-4 h-4" /> {t("coach.send")}</button>
            </form>
          </div>
          <aside className="space-y-4" data-testid="coach-recommendations">
            <div className="card-surface p-4">
              <div className="text-xs uppercase tracking-[0.24em] gold-text">{t("coach.recs")}</div>
              {!recs && <div className="text-white/60 text-sm mt-3">{t("coach.recommendations_appear")}</div>}
              {recs?.articles?.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs text-white/55 uppercase tracking-wider mb-2">{t("coach.articles_label")}</div>
                  {recs.articles.map((a) => (
                    <Link key={a.id} to={`/article/${a.slug}`} className="flex gap-3 p-2 rounded-md hover:bg-white/5" data-testid={`rec-article-${a.slug}`}>
                      <img src={a.image} alt="" className="w-14 h-14 rounded-md object-cover" />
                      <div className="text-sm leading-tight">{a.title}</div>
                    </Link>
                  ))}
                </div>
              )}
              {recs?.ebooks?.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs text-white/55 uppercase tracking-wider mb-2">{t("coach.ebooks_label")}</div>
                  {recs.ebooks.map((a) => (
                    <Link key={a.id} to="/shop" className="flex gap-3 p-2 rounded-md hover:bg-white/5" data-testid={`rec-ebook-${a.id}`}>
                      <img src={a.image} alt="" className="w-14 h-14 rounded-md object-cover" />
                      <div className="text-sm leading-tight">{a.title}</div>
                    </Link>
                  ))}
                </div>
              )}
              {recs?.workouts?.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs text-white/55 uppercase tracking-wider mb-2">{t("coach.workouts_label")}</div>
                  {recs.workouts.map((a) => (
                    <div key={a.id} className="flex gap-2 items-center p-2" data-testid={`rec-workout-${a.id}`}>
                      <LinkIcon className="w-3 h-3 gold-text" /><div className="text-sm">{a.title}</div>
                    </div>
                  ))}
                </div>
              )}
              {recs?.workshops?.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs text-white/55 uppercase tracking-wider mb-2">{t("coach.workshops_label")}</div>
                  {recs.workshops.map((a) => (
                    <div key={a.id} className="flex gap-2 items-center p-2">
                      <LinkIcon className="w-3 h-3 gold-text" /><div className="text-sm">{a.title}</div>
                    </div>
                  ))}
                </div>
              )}
              {recs?.mental_health_resources?.length > 0 && (
                <div className="mt-3 border-t border-white/10 pt-3" data-testid="coach-crisis-resources">
                  <div className="text-xs text-rose-300 uppercase tracking-wider mb-2 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {t("coach.crisis_support")}</div>
                  {recs.mental_health_resources.map((r, i) => (
                    <div key={i} className="text-sm py-1">
                      <div className="font-medium">{r.name}</div>
                      <div className="text-white/60 text-xs">{r.phone || r.sms || r.url}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </aside>
        </div>
      </Section>

      {/* CONNECT-WITH-COACH CTA — appears after the conversation has substance */}
      {(messages.filter((m) => m.role === "user").length >= 3 || crisis) && (
        <PractitionerRecommendations
          eyebrow={t("coach.real_coach_eyebrow")}
          title={t("coach.real_coach_title")}
          subtitle={
            crisis
              ? t("coach.real_coach_crisis_subtitle")
              : t("coach.real_coach_subtitle")
          }
          topic={crisis ? "mental-health" : undefined}
          limit={3}
          testid="coach-practitioner-recs"
        />
      )}
    </div>
  );
}
